import { cn } from "@/lib/utils";

type ButtonProps = {
  label?: string;
  children?: React.ReactNode;
  onClick?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  type?: "button" | "reset" | "submit";
  variant?: "primary" | "green" | "dark" | "outlinePrimary" | "outlineGreen" | "outlineDark";
  size?: "default" | "small";
};

function Spinner() {
  return (
    <svg
      className="w-4 h-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.196a8 8 0 01-8-8V0C5.373 0 0 5.373 0 12h4zm2 5.196a8 8 0 008-8V0c-5.523 0-10 4.477-10 10h4z"
      />
    </svg>
  );
}

export function Button({
  label,
  children,
  onClick,
  isLoading,
  disabled,
  className,
  type = "button",
  variant = "primary",
  size = "default",
}: ButtonProps) {
  const variantClasses = {
    primary: "bg-primary text-white hover:bg-primary/90",
    green: "bg-green text-white hover:bg-green/90",
    dark: "bg-dark text-white dark:bg-white/10 hover:bg-dark/90",
    outlinePrimary: "border border-primary hover:bg-primary/10 text-primary",
    outlineGreen: "border border-green hover:bg-green/10 text-green",
    outlineDark: "border border-dark hover:bg-dark/10 text-dark dark:border-white/25 dark:text-white dark:hover:bg-white/10",
  };

  const sizeClasses = {
    default: "py-3 px-6",
    small: "py-2 px-4 text-sm",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50",
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {isLoading && <Spinner />}
      {children || label}
    </button>
  );
}
